import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showToast, organizationService } from '../../../services';
import { userActions } from '../../../actions';
import { useLoggedUser } from '../../../hooks/useLoggedUser';
import { setFaroUserContext, initLogRocket, identifyUser } from '../../../observability';

const LoginPage = () => {
    const [accesscode, setAccesscode] = useState(['', '', '', '', '']);
    const [gameCharacter, setGameCharacter] = useState('');
    const [gameTitle, setGameTitle] = useState('');
    const [isWaitingForActivation, setIsWaitingForActivation] = useState(false);
    const loggingIn = useSelector(state => state.authentication.loggingIn);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const storedUser = useLoggedUser();
    const retryTimeoutRef = useRef(null);
    const sessionExpiredToastShownRef = useRef(false);
    const idleTimeoutToastShownRef = useRef(false);

    useEffect(() => {
        if (location.state?.sessionExpired && !sessionExpiredToastShownRef.current) {
            sessionExpiredToastShownRef.current = true;
            showToast('Session expired. Please log in again.', 'info');
            history.replace('/login', {});
        }
        if (location.state?.idleTimeout && !idleTimeoutToastShownRef.current) {
            idleTimeoutToastShownRef.current = true;
            showToast('You have been logged out due to inactivity.', 'info');
            history.replace('/login', {});
        }
    }, [location, history]);

    useEffect(() => {
        if (storedUser?.data?.LoggedInStatus === "Y") {
            history.replace('/');
            return;
        }

        setAccesscode(['', '', '', '', '']);
        setIsWaitingForActivation(false);

        fetchLandingMedia();
        organizationService
            .fetchBranding()
            .then((branding) => {
                if (branding?.GAME_BROWSER_TITLE) {
                    document.title = branding.GAME_BROWSER_TITLE;
                }
            })
            .catch(() => {
                // Keep static fallback title if branding API is unavailable.
            });
    }, []);

    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                window.clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, []);

    const fetchLandingMedia = () => {
        dispatch(userActions.fetchLandingMedia())
            .then(response => {
                if (!Array.isArray(response)) return;
                response.forEach(media => {
                    if (media.Category === "gamecharacter") {
                        setGameCharacter(media.MediaURL);
                    } else if (media.Category === "gametitle") {
                        setGameTitle(media.MediaURL);
                    }
                });
            })
            .catch(error => {
                console.log('Error:: ', error);
            });
    };

    const handleChange = (e, index) => {
        const { value } = e.target;

        // Only allow digits and ensure it's a single character
        if (!/^[0-9]$/.test(value) && value !== '') return;

        // Update the specific digit at the specified index
        const newAccesscode = [...accesscode];
        newAccesscode[index] = value;
        setAccesscode(newAccesscode);

        // Move to the next input if there is one and the user has entered a value
        if (value && index < 4) {
            const nextInput = document.querySelector(`input[name="code[]"]:nth-child(${index + 2})`);
            if (nextInput) nextInput.focus();
        }

        // Call handleSubmit if all digits are filled
        if (newAccesscode.every(char => char !== '')) {
            handleSubmit(newAccesscode.join(''));
        }
    };

    const handleKeyDown = (e, index) => {
        // Handle Backspace/Delete behavior
        if (e.key === 'Backspace' && !accesscode[index]) {
            // Move focus to the previous input if backspacing and it's empty
            if (index > 0) {
                const prevInput = document.querySelector(`input[name="code[]"]:nth-child(${index})`);
                if (prevInput) prevInput.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text');
        const digits = paste.replace(/\D/g, '').slice(0, 5);

        if (digits.length === 5) {
            const newAccesscode = digits.split('');
            setAccesscode(newAccesscode);
            handleSubmit(digits);

            // Focus the last field
            const lastInput = document.querySelector(`input[name="code[]"]:nth-child(5)`);
            if (lastInput) lastInput.focus();
        }
    };


    const handleSubmit = (code) => {
        if (code.length === 5) {
            // Close the keypad in iPad
            document.activeElement.blur();

            const retryLogin = () => {
                dispatch(userActions.login(code))
                    .then(response => {
                        if (!response || typeof response !== 'object') {
                            throw response;
                        }

                        if (response.responseMessage === "Class is not yet Activated") {
                            setIsWaitingForActivation(true);
                            if (retryTimeoutRef.current) window.clearTimeout(retryTimeoutRef.current);
                            retryTimeoutRef.current = window.setTimeout(retryLogin, 60000);
                            return;
                        }

                        if (response.responseMessage === "Class Not Yet started") {
                            setIsWaitingForActivation(false);
                            showToast(response.responseMessage, 'warn');
                            return;
                        }

                        if (response.responseMessage === "Class Ended") {
                            setIsWaitingForActivation(false);
                            showToast(response.responseMessage, 'warn');
                            return;
                        }

                        setIsWaitingForActivation(false);
                        localStorage.setItem('user', JSON.stringify(response));
                        setFaroUserContext({
                            user_id: response.data?.StudentID ?? response.data?.id ?? undefined,
                            tenant_id: response.data?.tenantId ?? response.data?.TenantId ?? undefined,
                        });
                        // Start LogRocket session replay only after successful login
                        initLogRocket();
                        const userId = response.data?.StudentProfileID ?? response.data?.StudentID ?? response.data?.id;
                        const firstName = response.data?.FirstName ?? '';
                        const lastName = response.data?.LastName ?? '';
                        identifyUser(userId != null ? String(userId) : undefined, {
                            name: [firstName, lastName].filter(Boolean).join(' ').trim() || undefined,
                            FirstName: firstName || undefined,
                            LastName: lastName || undefined,
                            StudentProfileID: response.data?.StudentProfileID,
                            AccessCode: code,
                        });

                        if (response.data?.LoggedInStatus === "Y") {
                            history.push('/');
                            return;
                        }

                        if (response.data?.LoggedInStatus === "N") {
                            history.push('/register');
                            return;
                        }

                        // Invalid access code
                        showToast(
                            response.errorMessage || 'Invalid Access Code',
                            'error'
                        );
                    })
                    .catch(error => {
                        setIsWaitingForActivation(false);
                        showToast(
                            typeof error === 'string'
                                ? error
                                : error?.message || 'Login failed',
                            'error'
                        );
                    });
            };

            retryLogin();
        }
    };

    return (
        <React.Fragment>
            <section className=" main-page">
                <div className="continer-fluid ">
                    <div className="content" id="content">
                        <div className="d-flex justify-content-center align-items-center flex-column">
                            {gameCharacter && (
                                <div className="main-ch-img drop-in">
                                    <img src={gameCharacter} alt="Game Character" />
                                </div>
                            )}
                            {gameTitle && (
                                <div className='game-tittle drop-in-text'>
                                    <img src={gameTitle} alt="Game Title" />
                                </div>
                            )}
                        </div>
                        <div className="pascode-card text-center">
                            {isWaitingForActivation ? (
                                <p className="b-font activation-msg" style={{ marginTop: '10px', wordBreak: 'break-word' }}>
                                    Please wait until the game is activated...
                                </p>
                            ) : (
                                <>
                                    <h5 className="b-font passcode" >Enter Passcode</h5>
                                    <div className="text-boxes">
                                        {accesscode.map((code, index) => (
                                            <input
                                                key={index}
                                                autoComplete="off"
                                                className="code"
                                                type="tel"
                                                maxLength="1"
                                                name="code[]"
                                                placeholder="..."
                                                value={code}
                                                onChange={(e) => handleChange(e, index)} // Handle input change
                                                onKeyDown={(e) => handleKeyDown(e, index)} // Handle backspace/delete
                                                onPaste={(e) => handlePaste(e)}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    );
};

export { LoginPage };